import type { PlanDay } from '../types';

const DAY_MS = 86_400_000;

export const OFFICIAL_2026: Record<string, { service: boolean; label: string }> = {
  '2026-01-01': { service: false, label: '元旦假期' },
  '2026-01-02': { service: false, label: '元旦假期' },
  '2026-01-03': { service: false, label: '元旦假期' },
  '2026-01-04': { service: true, label: '元旦调休工作日' },
  '2026-02-14': { service: true, label: '春节调休工作日' },
  '2026-02-15': { service: false, label: '春节假期' },
  '2026-02-16': { service: false, label: '春节假期' },
  '2026-02-17': { service: false, label: '春节假期' },
  '2026-02-18': { service: false, label: '春节假期' },
  '2026-02-19': { service: false, label: '春节假期' },
  '2026-02-20': { service: false, label: '春节假期' },
  '2026-02-21': { service: false, label: '春节假期' },
  '2026-02-22': { service: false, label: '春节假期' },
  '2026-02-23': { service: false, label: '春节假期' },
  '2026-02-28': { service: true, label: '春节调休工作日' },
  '2026-04-04': { service: false, label: '清明节假期' },
  '2026-04-05': { service: false, label: '清明节假期' },
  '2026-04-06': { service: false, label: '清明节假期' },
  '2026-05-01': { service: false, label: '劳动节假期' },
  '2026-05-02': { service: false, label: '劳动节假期' },
  '2026-05-03': { service: false, label: '劳动节假期' },
  '2026-05-04': { service: false, label: '劳动节假期' },
  '2026-05-05': { service: false, label: '劳动节假期' },
  '2026-05-09': { service: true, label: '劳动节调休工作日' },
  '2026-06-19': { service: false, label: '端午节假期' },
  '2026-06-20': { service: false, label: '端午节假期' },
  '2026-06-21': { service: false, label: '端午节假期' },
  '2026-09-20': { service: true, label: '国庆节调休工作日' },
  '2026-09-25': { service: false, label: '中秋节假期' },
  '2026-09-26': { service: false, label: '中秋节假期' },
  '2026-09-27': { service: false, label: '中秋节假期' },
  '2026-10-01': { service: false, label: '国庆节假期' },
  '2026-10-02': { service: false, label: '国庆节假期' },
  '2026-10-03': { service: false, label: '国庆节假期' },
  '2026-10-04': { service: false, label: '国庆节假期' },
  '2026-10-05': { service: false, label: '国庆节假期' },
  '2026-10-06': { service: false, label: '国庆节假期' },
  '2026-10-07': { service: false, label: '国庆节假期' },
  '2026-10-10': { service: true, label: '国庆节调休工作日' },
};

export function parseLocalDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
}

export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDays(value: string | Date, amount: number): Date {
  const date = typeof value === 'string' ? parseLocalDate(value) : new Date(value);
  date.setDate(date.getDate() + amount);
  return date;
}

export function mondayOf(date: Date): Date {
  const result = new Date(date);
  const day = result.getDay();
  result.setDate(result.getDate() - (day === 0 ? 6 : day - 1));
  result.setHours(12, 0, 0, 0);
  return result;
}

export function nextWeekStart(now = new Date()): string {
  return toIsoDate(addDays(mondayOf(now), 7));
}

export function shiftWeek(weekStart: string, amount: number): string {
  return toIsoDate(addDays(weekStart, amount * 7));
}

export function createWeekDays(weekStart: string): PlanDay[] {
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(weekStart, index);
    const iso = toIsoDate(date);
    const official = OFFICIAL_2026[iso];
    const isWeekday = date.getDay() >= 1 && date.getDay() <= 5;
    return {
      date: iso,
      mealService: { breakfast: official?.service ?? isWeekday, lunch: official?.service ?? isWeekday },
      serviceEnabled: official?.service ?? isWeekday,
      source: official ? 'official' : 'default',
      label: official?.label,
      selections: [],
    };
  });
}

export function weekEnd(weekStart: string): string {
  return toIsoDate(addDays(weekStart, 6));
}

export function formatShortDate(value: string): string {
  const date = parseLocalDate(value);
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

export function formatWeekRange(start: string, end = weekEnd(start)): string {
  const a = parseLocalDate(start);
  const b = parseLocalDate(end);
  if (a.getFullYear() === b.getFullYear()) {
    return `${a.getFullYear()}年${a.getMonth() + 1}月${a.getDate()}日—${b.getMonth() + 1}月${b.getDate()}日`;
  }
  return `${a.getFullYear()}年${a.getMonth() + 1}月${a.getDate()}日—${b.getFullYear()}年${b.getMonth() + 1}月${b.getDate()}日`;
}

export function weekdayLabel(value: string): string {
  return ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][parseLocalDate(value).getDay()];
}

export function isCalendarConfigured(weekStart: string): boolean {
  return parseLocalDate(weekStart).getFullYear() === 2026;
}

export function compareDates(a: string, b: string): number {
  return (parseLocalDate(a).getTime() - parseLocalDate(b).getTime()) / DAY_MS;
}
